/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import { getLocalStorage, setLocalStorage } from './localStorageAccess';
import { popAutoJoinApp } from './autoJoinApp';
import {
  getSessionStorage,
  removeSessionStorage,
  setSessionStorage,
} from './sessionStorageAccess';

const useNativeKey = 'useNative';
const wasNativeApplicationUsedInTheLast30DaysKey = 'lastUsage';
const wasManualSecondJoinOfferedInThisSessionKey = 'sessionUsage';
const numberOfMillisecondsInADay = 1000 * 60 * 60 * 24.0;

export const disableUseNative = (): void => {
  setLocalStorage(useNativeKey, 'false');
};

export const enableUseNative = (): void => {
  setLocalStorage(useNativeKey, 'true');
};

export const setManualSecondJoinOfferedInThisSession = (): void => {
  setSessionStorage(wasManualSecondJoinOfferedInThisSessionKey, 'true');
};

export const removeManualSecondJoinOfferedInThisSession = (): void => {
  removeSessionStorage(wasManualSecondJoinOfferedInThisSessionKey);
};

export const wasManualSecondJoinOfferedInThisSession = (): boolean => {
  return (
    getSessionStorage(wasManualSecondJoinOfferedInThisSessionKey) === 'true'
  );
};

export const setWasNativeApplicationUsedInTheLast30Days = (): void => {
  setLocalStorage(
    wasNativeApplicationUsedInTheLast30DaysKey,
    Date.now().toString()
  );
};

export const wasNativeApplicationUsedInTheLast30Days = (): boolean => {
  const lastTimestamp = getLocalStorage(
    wasNativeApplicationUsedInTheLast30DaysKey
  );
  return (
    (Date.now() - parseInt(lastTimestamp ?? '0')) / numberOfMillisecondsInADay <
    30
  );
};

enum UseNativeState {
  Active = 'active',
  Disabled = 'disabled',
  Undefined = 'undefined',
}

const getUseNativeState = (): UseNativeState => {
  const useNative = getLocalStorage(useNativeKey);
  const autoJoinApp = popAutoJoinApp();
  switch (useNative) {
    case 'true':
      return UseNativeState.Active;
    case 'false':
      return UseNativeState.Disabled;
    default:
      if (!autoJoinApp) {
        disableUseNative();
        return UseNativeState.Disabled;
      }
      return UseNativeState.Undefined;
  }
};

export const initializeNativeJoinSuccess = (): void => {
  if (getUseNativeState() === UseNativeState.Undefined) {
    enableUseNative();
  }
  setWasNativeApplicationUsedInTheLast30Days();
  removeManualSecondJoinOfferedInThisSession();
};

export const shouldUseNative = (): boolean => {
  return getUseNativeState() !== UseNativeState.Disabled;
};

export const hadNativeJoinSuccessInPast = (): boolean => {
  return getUseNativeState() !== UseNativeState.Undefined;
};
